import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';
import { Button } from '@peacecover/component';
import { ArrowRightSmall, GiftFillDisabled } from '@peacecover/icon';
import { useGetBettieDailyCheckInStatus } from '@pocapan/shared/domains/bettie/ui/hooks/useBettieDailyCheckInStatus';
import { useClaimBettieDailyCheckInReward } from '@pocapan/shared/domains/bettie/ui/hooks/useClaimBettieDailyCheckInReward';
import { useFeatureEnabled } from '@pocapan/shared/domains/features/ui/hooks/useFeatureEnabled';
import { FEATURE_KEYS } from '@pocapan/shared/domains/features/constants';
import { ReactComponent as BlueGiftFill } from '../../../icons/blue.gift.svg';

const BettieFreeChargeSection = () => {
  const [isCheckInPopupOpen, setIsCheckInPopupOpen] = useState(false);
  const navigate = useNavigate();
  const { data: bettieDailyCheckInStatus, isLoading } = useGetBettieDailyCheckInStatus();
  const { mutateAsync: claimBettieDailyCheckInReward } = useClaimBettieDailyCheckInReward();
  const { data, isLoading: isBettieRewardsLoading } = useFeatureEnabled(
    FEATURE_KEYS.BETTIE_REWARDS
  );

  const isBettieRewardsEnabled = data?.featureFlag.enabled;

  useEffect(() => {
    if (!isCheckInPopupOpen) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isCheckInPopupOpen]);

  if (isLoading || isBettieRewardsLoading) {
    return <div className="h-[33px] w-full" />;
  }

  const handleCheckIn = async () => {
    try {
      const response = await claimBettieDailyCheckInReward();
      if (response.bettieTransactions) {
        setIsCheckInPopupOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {isBettieRewardsEnabled ? (
        <button
          type="button"
          className="mt-[17px] flex items-center gap-[4px] justify-center mx-auto"
          onClick={() => navigate('/free-charge')}
        >
          <BlueGiftFill width={16} height={16} />
          <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
            {t('BettieChatList.freeCharge')}
          </p>
        </button>
      ) : !bettieDailyCheckInStatus ? (
        <button
          type="button"
          onClick={handleCheckIn}
          className="mt-[17px] flex items-center gap-[4px] justify-center mx-auto"
        >
          <BlueGiftFill width={16} height={16} />
          <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
            {t('BettieChatList.freeChargeWithAttendance')}
          </p>
          <ArrowRightSmall size={16} className="text-text-main-lv4" />
        </button>
      ) : (
        <div className="flex items-center gap-[4px] mt-[17px] justify-center">
          <GiftFillDisabled size={16} className="text-text-sub-lv3" />
          <p className="text-[13px] font-medium leading-[16px] text-text-sub-lv3">
            {t('BettieChatList.AlreadyGetFreeCharge')}
          </p>
        </div>
      )}
      <AnimatePresence>
        {isCheckInPopupOpen && (
          <motion.div
            className="fixed top-0 left-0 w-full h-full bg-black/50 z-[999] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-[311px] px-[14px] pt-[32px] pb-[16px] flex flex-col items-center gap-[32px] bg-background-light dark:bg-background-dark rounded-[24px]"
              initial={{ scale: 0.95, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24, duration: 0.2 }}
            >
              <img
                src="/assets/icons/check_calendar.png"
                alt="free_charge_gift"
                className="w-[145px] h-[130px]"
              />
              <div className="flex flex-col items-center gap-[8px]">
                <p className="text-[17px] font-bold leading-[20px] text-text-main-lv1">
                  {t('BettieChatList.completeAttendanceCheck')}
                </p>
                <p className="text-[15px] leading-[18px] text-text-main-lv4 text-center whitespace-pre-line">
                  {t('BettieChatList.waitingForFreeCharge')}
                </p>
              </div>
              <Button
                size="large"
                className="text-[17px] font-semibold w-full"
                onClick={() => setIsCheckInPopupOpen(false)}
              >
                {t('confirm')}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BettieFreeChargeSection;
